'use client'

import { useState } from 'react'
import type { ResourceAccordionSectionFragment } from '@/lib/types/datocms/graphql/generated'
import { TextWithHighlight } from '../TextWithHighlight'
import Eyebrow from '../ui/Eyebrow'
import Manual from '../Manual'
import clsx from 'clsx'
import { isStructuredTextEmpty } from '@/utils/datocms/isStructuredTextEmpty'
import PlusMinus from '../svg/icon/PlusMinus'

export default function ResourceAccordionSection({
	title,
	tag,
	accordion,
}: Readonly<ResourceAccordionSectionFragment>) {
	const [activeAccordion, setActiveAccordion] = useState<string | null>(null)

	const handleAccordionClick = (id: string) => {
		if (activeAccordion === id) {
			setActiveAccordion(null)
		} else {
			setActiveAccordion(id)
		}
	}

	return (
		<section className="resource-accordion-section @container">
			<div className="relative bg-white common-x-padding py-16 @5xl:py-24 overflow-hidden max-w-screen-2xl mx-auto">
				{tag && <Eyebrow>{tag}</Eyebrow>}
				{title && !isStructuredTextEmpty(title.text) && (
					<div className="heading-size-3 mt-6 mb-12 @5xl:mb-16">
						<TextWithHighlight text={title} />
					</div>
				)}

				{accordion.map((accordionCard) => (
					<div key={accordionCard.id as string} className="bg-white-frosted rounded-lg mt-5">
						<button
							className={clsx(
								'relative flex justify-between items-center text-left py-6 @5xl:py-10 px-4 @5xl:px-6 w-full hover:bg-white-frosted-shade transition-colors duration-300 ease-out',
								activeAccordion === accordionCard.id
									? 'bg-white-frosted-shade rounded-t-lg'
									: 'bg-white-frosted rounded-lg'
							)}
							onClick={() => handleAccordionClick(accordionCard.id)}
						>
							<h3 className="heading-size-5">{accordionCard.title}</h3>
							<PlusMinus className="w-6 h-6" isOpen={activeAccordion === accordionCard.id} />
						</button>

						{/* Manuals */}
						<div
							className={clsx(
								'grid px-4 @5xl:px-6 transition-all duration-300 ease-s overflow-hidden',
								activeAccordion === accordionCard.id ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
							)}
						>
							<div className="overflow-hidden">
								{accordionCard.manuals.map((manual) => (
									<Manual key={manual.id as string} manual={manual} />
								))}
							</div>
						</div>
					</div>
				))}
			</div>
		</section>
	)
}
